import {
  useState,
  type FormEvent,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router';

import { AppShell } from '../layout/AppShell';
import {
  authenticatedReturnFromState,
  protectedLocationReturn,
} from './redirects';
import { authenticationErrorMessage } from './errors';
import { useAuthentication } from './authState';
import { canReviewCurveLibrary } from './curveLibraryAccess';
import { WorkspaceAccount } from './WorkspaceAccount';

interface AuthCardProps extends PropsWithChildren {
  description: string;
  eyebrow: string;
  heading: string;
}

function AuthCard({
  children,
  description,
  eyebrow,
  heading,
}: AuthCardProps) {
  return (
    <div className="auth-page">
      <main id="main-content" className="auth-page__main">
        <Link className="auth-brand" to="/" aria-label="Flow Wizard public home">
          <span className="brand__mark" aria-hidden="true">
            FW
          </span>
          <span>
            <strong>Flow Wizard</strong>
            <small>Water-transfer planning</small>
          </span>
        </Link>
        <section className="auth-card" aria-labelledby="auth-heading">
          <p className="eyebrow">{eyebrow}</p>
          <h1 id="auth-heading">{heading}</h1>
          <p className="auth-card__description">{description}</p>
          {children}
        </section>
      </main>
    </div>
  );
}

function FormError({ children }: { children: ReactNode }) {
  return (
    <div className="auth-message auth-message--error" role="alert">
      {children}
    </div>
  );
}

function LoginScreen() {
  const { login, requestPasswordReset } = useAuthentication();
  const [view, setView] = useState<'forgot' | 'login'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    const submittedPassword = password;
    setPassword('');
    try {
      await login(email, submittedPassword);
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'Sign in failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitResetRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const response = await requestPasswordReset(email);
      setMessage(response.message);
    } catch (reason) {
      setError(
        authenticationErrorMessage(
          reason,
          'Password reset instructions could not be requested.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (view === 'forgot') {
    return (
      <AuthCard
        description="Enter your account email. The response is the same whether or not an active account matches."
        eyebrow="Account recovery"
        heading="Reset your password"
      >
        {message === null ? (
          <form className="auth-form" onSubmit={(event) => void submitResetRequest(event)}>
            <label>
              <span>Email address</span>
              <input
                autoComplete="email"
                inputMode="email"
                name="email"
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            {error === null ? null : <FormError>{error}</FormError>}
            <button className="button button--primary" disabled={submitting} type="submit">
              {submitting ? 'Sending…' : 'Send reset instructions'}
            </button>
          </form>
        ) : (
          <div className="auth-message auth-message--success" role="status">
            <h2>Check your email</h2>
            <p>{message}</p>
          </div>
        )}
        <button
          className="auth-link-button"
          type="button"
          onClick={() => {
            setError(null);
            setMessage(null);
            setView('login');
          }}
        >
          Back to sign in
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      description="Use the account provided by your Flow Wizard administrator."
      eyebrow="Secure workspace"
      heading="Sign in to Flow Wizard"
    >
      <form className="auth-form" onSubmit={(event) => void submitLogin(event)}>
        <label>
          <span>Email address</span>
          <input
            autoComplete="username"
            inputMode="email"
            name="email"
            required
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          <span>Password</span>
          <input
            autoComplete="current-password"
            name="password"
            required
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {error === null ? null : <FormError>{error}</FormError>}
        <button className="button button--primary" disabled={submitting} type="submit">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <button
        className="auth-link-button"
        type="button"
        onClick={() => {
          setError(null);
          setView('forgot');
        }}
      >
        Forgot password?
      </button>
    </AuthCard>
  );
}

function PasswordResetScreen() {
  const { clearPasswordReset, confirmPasswordReset } = useAuthentication();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < 15) {
      setError('Password must contain at least 15 characters.');
      return;
    }
    if (password !== confirmation) {
      setError('The passwords do not match.');
      return;
    }

    const submittedPassword = password;
    setPassword('');
    setConfirmation('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(submittedPassword);
    } catch (reason) {
      setError(
        authenticationErrorMessage(
          reason,
          'The password reset link is invalid or expired.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      description="Choose a new password for this account. Completing the reset signs out every existing session."
      eyebrow="Account recovery"
      heading="Choose a new password"
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label>
          <span>New password</span>
          <input
            aria-describedby="password-requirements"
            autoComplete="new-password"
            minLength={15}
            name="password"
            required
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <p id="password-requirements" className="auth-form__hint">
          Use at least 15 characters.
        </p>
        <label>
          <span>Confirm new password</span>
          <input
            autoComplete="new-password"
            minLength={15}
            name="password-confirmation"
            required
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </label>
        {error === null ? null : <FormError>{error}</FormError>}
        <button className="button button--primary" disabled={submitting} type="submit">
          {submitting ? 'Changing password…' : 'Change password'}
        </button>
      </form>
      <button className="auth-link-button" type="button" onClick={clearPasswordReset}>
        Back to sign in
      </button>
    </AuthCard>
  );
}

function PasswordResetCompleteScreen() {
  const { clearPasswordReset } = useAuthentication();

  return (
    <AuthCard
      description="Your old sessions have been signed out. Use the new password to continue."
      eyebrow="Password changed"
      heading="Your password has been changed"
    >
      <div className="auth-message auth-message--success" role="status">
        Your new password is ready to use.
      </div>
      <button
        className="button button--primary"
        type="button"
        onClick={clearPasswordReset}
      >
        Return to sign in
      </button>
    </AuthCard>
  );
}

function AuthenticationUnavailableScreen() {
  const { error, retry } = useAuthentication();

  return (
    <AuthCard
      description="The workspace remains locked until Flow Wizard can safely determine the server authentication mode."
      eyebrow="Connection problem"
      heading="Sign-in status is unavailable"
    >
      <FormError>{error ?? 'The authentication service is unavailable.'}</FormError>
      <button className="button button--primary" type="button" onClick={retry}>
        Try again
      </button>
    </AuthCard>
  );
}

function AuthenticationLoadingScreen() {
  return (
    <AuthCard
      description="Checking this server and your current session."
      eyebrow="Secure workspace"
      heading="Opening Flow Wizard"
    >
      <p className="auth-loading" role="status">
        Checking sign-in status…
      </p>
    </AuthCard>
  );
}

function SessionRevalidationStatus() {
  const { revalidation, retry } = useAuthentication();

  if (
    revalidation.status === 'idle' ||
    revalidation.status === 'checking'
  ) {
    return null;
  }

  return (
    <div
      className="auth-message auth-message--error session-revalidation"
      role="status"
      aria-live="polite"
    >
      <span>{revalidation.message}</span>
      {revalidation.status === 'failed' ? (
        <button className="auth-link-button" type="button" onClick={retry}>
          Retry connection
        </button>
      ) : null}
    </div>
  );
}

function AccountSlot() {
  const { logout, session } = useAuthentication();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  if (session === null) return null;

  async function signOut() {
    setError(null);
    setSigningOut(true);
    try {
      await logout();
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'Sign out failed. Please try again.'));
      setSigningOut(false);
    }
  }

  return (
    <div className="workspace-account-slot">
      <WorkspaceAccount
        manageLabel="Manage users"
        session={{
          expiresAt: session.idleExpiresAt,
          identity: {
            displayName: session.user.display_name,
            email: session.user.email,
            id: session.user.id,
          },
        }}
        onManageAccount={
          session.user.role === 'admin' ? () => navigate('/users') : undefined
        }
        onSignOut={signingOut ? undefined : () => void signOut()}
      />
      {error === null ? null : (
        <span className="workspace-account-slot__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function AuthenticationGateway() {
  const {
    mode,
    passwordResetComplete,
    passwordResetToken,
    session,
  } = useAuthentication();
  const location = useLocation();
  const authenticationPath = location.pathname.replace(/\/$/u, '');
  const isLoginPath = authenticationPath === '/login';
  const isResetPath = authenticationPath === '/reset-password';

  if (passwordResetComplete) return <PasswordResetCompleteScreen />;
  if (passwordResetToken !== null) return <PasswordResetScreen />;

  if (mode === 'checking') return <AuthenticationLoadingScreen />;
  if (mode === 'hosted-suspended') return <AuthenticationLoadingScreen />;
  if (mode === 'error') return <AuthenticationUnavailableScreen />;
  if (mode === 'hosted-anonymous') {
    if (isResetPath) return <Navigate replace to="/login" />;
    if (!isLoginPath) {
      return (
        <Navigate
          replace
          state={{ returnTo: protectedLocationReturn(location) }}
          to="/login"
        />
      );
    }
    return <LoginScreen />;
  }
  if (isLoginPath || isResetPath) {
    return (
      <Navigate
        replace
        to={authenticatedReturnFromState(location.state)}
      />
    );
  }
  if (mode === 'hosted-authenticated') {
    return (
      <>
        <SessionRevalidationStatus />
        <AppShell
          key={session?.user.id}
          account={<AccountSlot />}
          canManageUsers={session?.user.role === 'admin'}
          canReviewCurveLibrary={canReviewCurveLibrary({ mode, session })}
        />
      </>
    );
  }
  return <AppShell canReviewCurveLibrary={canReviewCurveLibrary({ mode, session })} />;
}
